import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { OperationLogSource } from '@/client/operation-log';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { useAzureADHandler } from '@/hooks/integrations/useAzureAD.hook';
import { useGoogleWorkspaceSyncHandler } from '@/hooks/integrations/useGoogleWorkspaceSync.hook';
import { useOperationLogs } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useUsersImportFlow } from '@/hooks/useUsersImportFlow';
import { CsvErrorsSection } from '@/pages/accounts/account-errors/CsvErrorsSection';
import {
  EmptyStateCard,
  EmptyStateContent,
  PageContainer,
} from '@/pages/accounts/account-errors/error-log.styled';
import { SyncErrorsSection } from '@/pages/accounts/account-errors/SyncErrorsSection';
import { selectCurrentAccount } from '@/store/features/account';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { FlexContainer } from '@/ui/styled-ui';
import { hasCurrentOperationLogErrors } from '@/utils/helpers';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const pathItems = (
  accountName: string | undefined,
  accountId: string | undefined,
  t: (key: string) => string,
): MenuItem[] => {
  const items: MenuItem[] = [];

  if (accountName && accountId) {
    items.push({
      label: accountName,
      url: RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](accountId),
      template: AppBreadCrumbTemplate,
    });
  }

  items.push({
    label: t('errorLog.title'),
    url: RedirectPaths[RedirectPathsEnum.ACCOUNT_ERRORS](),
    template: AppBreadCrumbTemplate,
  });

  return items;
};

export const AccountErrorsPage = () => {
  const { t } = useTranslation();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const navigate = useNavigate();
  const { renderFileUpload, importModal } = useUsersImportFlow({});
  const { operationLogs, isLoading } = useOperationLogs(currentAccount?.id);
  const sourcesWithErrorsOnMount = useRef<Set<OperationLogSource> | null>(null);
  const { handleForceADSync, syncState } = useAzureADHandler(
    currentAccount?.id as string,
  );
  const { handleForceGoogleSync, syncState: googleSyncState } =
    useGoogleWorkspaceSyncHandler();

  const csvLog = operationLogs?.result?.find(
    (log) => log.source === OperationLogSource.MANUAL_USER_IMPORT,
  );
  const adLog = operationLogs?.result?.find(
    (log) => log.source === OperationLogSource.AD_SYNC,
  );
  const googleLog = operationLogs?.result?.find(
    (log) => log.source === OperationLogSource.GOOGLE_WORKSPACE_SYNC,
  );

  const goToPendingDeletion = () =>
    navigate(
      `${RedirectPaths[RedirectPathsEnum.USERS]()}?status=pending-deletion`,
    );

  if (
    !isLoading &&
    operationLogs &&
    sourcesWithErrorsOnMount.current === null
  ) {
    sourcesWithErrorsOnMount.current = new Set(
      operationLogs.result
        ?.filter((log) => hasCurrentOperationLogErrors(log, currentAccount))
        .map((log) => log.source) ?? [],
    );
  }

  if (isLoading) {
    return (
      <FlexContainer direction="column" className="mt-5">
        <ProgressSpinner />
      </FlexContainer>
    );
  }

  const showCsvSection =
    hasCurrentOperationLogErrors(csvLog, currentAccount) ||
    !!sourcesWithErrorsOnMount.current?.has(
      OperationLogSource.MANUAL_USER_IMPORT,
    );

  const showAdSection =
    hasCurrentOperationLogErrors(adLog, currentAccount) ||
    !!sourcesWithErrorsOnMount.current?.has(OperationLogSource.AD_SYNC);

  const showGoogleSection =
    hasCurrentOperationLogErrors(googleLog, currentAccount) ||
    !!sourcesWithErrorsOnMount.current?.has(
      OperationLogSource.GOOGLE_WORKSPACE_SYNC,
    );

  return (
    <PageContainer direction="column" align="flex-start">
      <AppBreadCrumb
        model={pathItems(currentAccount?.name, currentAccount?.id, t)}
      />

      <FlexContainer justify="space-between">
        <h1 className="mt-2 mb-2">{t('errorLog.title')}</h1>
      </FlexContainer>

      {showCsvSection && (
        <CsvErrorsSection
          log={csvLog}
          renderFileUpload={renderFileUpload}
          t={t}
        />
      )}

      {showAdSection && (
        <SyncErrorsSection
          log={adLog}
          source={OperationLogSource.AD_SYNC}
          t={t}
          onSync={() => handleForceADSync(currentAccount?.id as string)}
          syncState={syncState}
          onGoToSettings={() =>
            navigate(
              RedirectPaths[RedirectPathsEnum.ACCOUNT_INTEGRATIONS_MICROSOFT](),
            )
          }
          onGoToPendingDeletion={goToPendingDeletion}
        />
      )}

      {showGoogleSection && (
        <SyncErrorsSection
          log={googleLog}
          source={OperationLogSource.GOOGLE_WORKSPACE_SYNC}
          t={t}
          onSync={() => handleForceGoogleSync(currentAccount?.id as string)}
          syncState={googleSyncState}
          onGoToSettings={() =>
            navigate(
              RedirectPaths[RedirectPathsEnum.ACCOUNT_INTEGRATIONS_GOOGLE](),
            )
          }
          onGoToPendingDeletion={goToPendingDeletion}
        />
      )}

      {!showCsvSection && !showAdSection && !showGoogleSection && (
        <EmptyStateCard>
          <EmptyStateContent>
            <i className="pi pi-check-circle" />
            <span>{t('errorLog.noCurrentErrors')}</span>
          </EmptyStateContent>
        </EmptyStateCard>
      )}

      {importModal}
    </PageContainer>
  );
};
